import React, { useEffect, useRef } from 'react';
import { Hyperlink } from '@THC/components/Hyperlink';
import { MDText } from 'i18n-react';
import Link from 'next/link';

import { Modal } from '@motorway/motorway-storybook-cra';
import { toast } from '@motorway/mw-highway-code/pre-alpha';

import { triggerBulkBiddingBidsOfferLinkClicked } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingBidsOfferLinkClicked';
import { triggerBulkBiddingModalClosed } from 'Services/analytics/events/bulkBiddingEvents/triggerBulkBiddingModalClosed';
import { AccountCategory, AccountSlugs } from 'Types/account';
import ROUTES from 'Utilities/routes';

import bulkBiddingModalTexts from '../BulkBidding.texts';
import type { GetVehiclesForBulkBidding } from '../useFetchVehiclesForBulkBiddingCSV';

import { ConfirmationView } from './ConfirmationView/ConfirmationView';
import { ProcessingBidsView } from './ProcessingBidsView/ProcessingBidsView';
import { ResultsView } from './ResultsView/ResultsView';
import { useBulkBidding } from './useBulkBidding';
import type { SuccessfulBid } from './useBulkBidding.types';
import { BULK_BIDDING_VIEW_NAMES, useBulkBiddingModalViewName } from './useBulkBiddingModalViewName';

import styles from './BulkBiddingModal.module.scss';

const LocalT = new MDText(bulkBiddingModalTexts);

interface BulkBiddingModalProps {
	handleDismissModal: () => void;
	vehicles: GetVehiclesForBulkBidding;
}

const BidsAndOffersLink: React.FC = () => (
	<Link legacyBehavior passHref href={ROUTES.ACCOUNT.as(AccountCategory.BIDS, AccountSlugs.ACTIVE)}>
		<Hyperlink
			label={LocalT.translate('toast.successful.bidsAndOffers')}
			onClick={() => triggerBulkBiddingBidsOfferLinkClicked('Success toast')}
		/>
	</Link>
);

const createAllSuccessfulCallback = (handleDismissModal: () => void) => (results: SuccessfulBid[]) => {
	handleDismissModal();
	toast({
		content: LocalT.translate('toast.successful.content', {
			bidsAndOffers: <BidsAndOffersLink />,
			heading: LocalT.translate('toast.successful.heading', { successfulBidsLength: results.length }),
		}),
		variant: 'success',
	});
};

export const BulkBiddingModal: React.FC<BulkBiddingModalProps> = ({ handleDismissModal, vehicles }) => {
	const onAllSuccessful = createAllSuccessfulCallback(handleDismissModal);
	const abortControllerRef = useRef<AbortController>(new AbortController());

	const { bidResults, bidsProcessedCount, handleCreateBids, isCreatingBids } = useBulkBidding({
		onAllSuccessful,
		signal: abortControllerRef.current.signal,
	});
	const bulkBiddingModalViewName = useBulkBiddingModalViewName({ bidResults, isCreatingBids });

	useEffect(() => {
		const controller = abortControllerRef.current;
		return () => {
			return controller.abort();
		};
	}, []);

	const handleCreateBulkBids = async () => {
		await handleCreateBids(vehicles);
	};

	return (
		<Modal
			contentClass={styles.modalContent}
			dialogClass={styles.modalDialog}
			dismissModal={() => {
				triggerBulkBiddingModalClosed(bulkBiddingModalViewName);
				handleDismissModal();
			}}
			shouldCloseOnOverlayClick={false}
			withPanelStyles={false}
		>
			{(() => {
				switch (bulkBiddingModalViewName) {
					case BULK_BIDDING_VIEW_NAMES.CONFIRMATION:
						return (
							<ConfirmationView
								actionableVehicles={vehicles.actionable}
								handleCreateBids={handleCreateBulkBids}
								handleDismissModal={handleDismissModal}
								isCreatingBids={isCreatingBids}
								unactionableVehicles={vehicles.unactionable}
							/>
						);

					case BULK_BIDDING_VIEW_NAMES.PROCESSING:
						return (
							<ProcessingBidsView
								bidsProcessedCount={bidsProcessedCount}
								totalBidsCount={vehicles.actionable.length + vehicles.unactionable.length}
							/>
						);

					case BULK_BIDDING_VIEW_NAMES.RESULTS:
						return bidResults ? <ResultsView bidResults={bidResults} /> : null;
					default:
						return null;
				}
			})()}
		</Modal>
	);
};
